import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Plus, ArrowLeft, Save, Eye, Trash2, ChevronUp, ChevronDown, ExternalLink,
  Globe, FileText, Layers, Video, Quote, Image as ImageIcon, Layout, Send, Minus,
} from 'lucide-react';
import BlockRenderer, { resolveVideoEmbed } from '../../components/cms/BlockRenderer';
import ImagePicker from '../../components/cms/ImagePicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Master block definitions — type, label, icon, default data
const BLOCK_DEFS = [
  { type: 'hero', label: 'Hero', icon: Layout, defaults: { eyebrow: '', heading: 'Headline goes here', subheading: '', image_url: '', primary_cta_label: '', primary_cta_url: '', secondary_cta_label: '', secondary_cta_url: '' } },
  { type: 'rich_text', label: 'Rich text', icon: FileText, defaults: { heading: '', body_markdown: '' } },
  { type: 'service_grid', label: 'Service grid (3-col)', icon: Layers, defaults: { heading: '', subheading: '', items: [{title:'Item 1',description:'',image_url:'',link_url:''}] } },
  { type: 'quote', label: 'Quote', icon: Quote, defaults: { quote: '', attribution: '' } },
  { type: 'video', label: 'Video embed', icon: Video, defaults: { heading: '', url: '', caption: '' } },
  { type: 'two_column', label: 'Two-column (text + image)', icon: ImageIcon, defaults: { heading: '', body_markdown: '', image_url: '', image_side: 'right' } },
  { type: 'cta_banner', label: 'CTA banner', icon: Send, defaults: { heading: '', subheading: '', cta_label: 'Learn more', cta_url: '/', background_color: '' } },
  { type: 'contact_form', label: 'Contact form', icon: Send, defaults: { heading: 'Contact Us', subheading: '', submit_label: 'Send' } },
  { type: 'spacer', label: 'Spacer', icon: Minus, defaults: { size: 'md' } },
];

export default function SiteAdmin() {
  const { pageId } = useParams();
  return pageId ? <PageEditor pageId={pageId} /> : <PagesList />;
}

// =============================================================
// PAGES LIST
// =============================================================
function PagesList() {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [nav, setNav] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [p, n] = await Promise.all([
        axios.get(`${API}/cms/pages`),
        axios.get(`${API}/cms/navigation`),
      ]);
      setPages(p.data || []);
      setNav(n.data?.items || []);
    } catch (e) {
      toast.error('Failed to load pages');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-semibold tracking-tight">Public site</h1>
          <p className="text-muted-foreground mt-1">
            Edit and publish pages on your public website. Changes go live the moment you click <strong>Publish</strong>.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => window.open('/', '_blank')} data-testid="view-live-site">
            <ExternalLink size={14} className="mr-1.5" /> View live site
          </Button>
          <Button onClick={() => setShowNew(true)} data-testid="new-page-btn">
            <Plus size={14} className="mr-1.5" /> New page
          </Button>
        </div>
      </div>

      {loading ? (
        <Card className="p-12 text-center text-muted-foreground">Loading…</Card>
      ) : (
        <>
          <Card className="overflow-hidden">
            <table className="min-w-full text-sm">
              <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Title</th>
                  <th className="px-3 py-2 text-left">URL</th>
                  <th className="px-3 py-2 text-left">Section</th>
                  <th className="px-3 py-2 text-left">Status</th>
                  <th className="px-3 py-2 text-left">Last updated</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {pages.map((p) => (
                  <tr key={p.id} className="border-t hover:bg-muted/20">
                    <td className="px-3 py-2 font-medium">{p.title}</td>
                    <td className="px-3 py-2 text-muted-foreground">/{p.slug === 'home' ? '' : p.slug}</td>
                    <td className="px-3 py-2 text-muted-foreground">{p.section || '—'}</td>
                    <td className="px-3 py-2">
                      {p.published ? (
                        <Badge className="bg-green-100 text-green-800 border-green-300 border">Published</Badge>
                      ) : (
                        <Badge variant="outline">Draft</Badge>
                      )}
                    </td>
                    <td className="px-3 py-2 text-muted-foreground text-xs">
                      {p.updated_at ? new Date(p.updated_at).toLocaleString() : '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => navigate(`/admin/site/pages/${p.id}`)} data-testid={`edit-page-${p.slug}`}>
                        Edit
                      </Button>
                    </td>
                  </tr>
                ))}
                {pages.length === 0 && (
                  <tr><td colSpan={6} className="px-3 py-10 text-center text-muted-foreground">No pages yet. Click "New page" to get started.</td></tr>
                )}
              </tbody>
            </table>
          </Card>

          <NavigationEditor nav={nav} onSaved={refresh} pages={pages} />
        </>
      )}

      {showNew && <NewPageDialog onClose={() => setShowNew(false)} onCreated={(p) => { setShowNew(false); refresh(); navigate(`/admin/site/pages/${p.id}`); }} />}
    </div>
  );
}

function NewPageDialog({ onClose, onCreated }) {
  const [form, setForm] = useState({ title: '', slug: '', section: '', meta_description: '' });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.slug) {
      toast.error('Title and URL slug are required');
      return;
    }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/cms/pages`, { ...form, blocks: [] });
      toast.success('Page created');
      onCreated(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create page');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4">
      <Card className="w-full max-w-lg p-6">
        <h2 className="font-heading text-2xl font-semibold mb-4">New page</h2>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <Label>Title *</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value, slug: f.slug || slugify(e.target.value) }))} data-testid="new-page-title" />
          </div>
          <div>
            <Label>URL slug *</Label>
            <Input value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} placeholder="e.g. testimonials or services/aquatic" data-testid="new-page-slug" />
            <p className="text-xs text-muted-foreground mt-1">Will be at <code>/{form.slug || 'your-slug'}</code></p>
          </div>
          <div>
            <Label>Section (optional, for nav grouping)</Label>
            <Input value={form.section} onChange={(e) => setForm((f) => ({ ...f, section: e.target.value }))} placeholder="e.g. Services" />
          </div>
          <div>
            <Label>SEO description</Label>
            <Textarea rows={2} value={form.meta_description} onChange={(e) => setForm((f) => ({ ...f, meta_description: e.target.value }))} />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
            <Button type="submit" className="flex-1" disabled={saving} data-testid="confirm-new-page">{saving ? 'Creating…' : 'Create'}</Button>
          </div>
        </form>
      </Card>
    </div>
  );
}

function slugify(s) {
  return String(s || '').toLowerCase().replace(/[^a-z0-9\s/-]/g, '').trim().replace(/\s+/g, '-').replace(/-+/g, '-');
}

// =============================================================
// PAGE EDITOR
// =============================================================
function PageEditor({ pageId }) {
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [showInsert, setShowInsert] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/cms/pages/${pageId}`);
      setPage(data);
      setBlocks(data.draft_blocks || data.blocks || []);
    } catch (e) {
      toast.error('Page not found');
      navigate('/admin/site');
    } finally {
      setLoading(false);
    }
  }, [pageId, navigate]);

  useEffect(() => { load(); }, [load]);

  const save = useCallback(async ({ silent } = {}) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/cms/pages/${pageId}`, {
        title: page.title,
        slug: page.slug,
        section: page.section,
        meta_description: page.meta_description,
        blocks,
      });
      setPage(data);
      if (!silent) toast.success('Draft saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  }, [pageId, page, blocks]);

  const publish = async () => {
    setPublishing(true);
    try {
      await axios.put(`${API}/cms/pages/${pageId}`, {
        title: page.title, slug: page.slug, section: page.section, meta_description: page.meta_description, blocks,
      });
      await axios.post(`${API}/cms/pages/${pageId}/publish`);
      toast.success('Published — your changes are live');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Publish failed');
    } finally {
      setPublishing(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`Delete page "${page.title}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/cms/pages/${pageId}`);
      toast.success('Page deleted');
      navigate('/admin/site');
    } catch (e) {
      toast.error('Delete failed');
    }
  };

  const updateBlock = (idx, patch) => {
    setBlocks((bs) => bs.map((b, i) => i === idx ? { ...b, data: { ...b.data, ...patch } } : b));
  };
  const moveBlock = (idx, dir) => {
    setBlocks((bs) => {
      const next = [...bs];
      const j = idx + dir;
      if (j < 0 || j >= next.length) return next;
      [next[idx], next[j]] = [next[j], next[idx]];
      return next;
    });
    setSelectedIdx((s) => (s == null ? s : s + dir));
  };
  const deleteBlock = (idx) => {
    setBlocks((bs) => bs.filter((_, i) => i !== idx));
    setSelectedIdx(null);
  };
  const insertBlock = (type, atIdx = null) => {
    const def = BLOCK_DEFS.find((d) => d.type === type);
    const block = { id: crypto.randomUUID(), type, data: JSON.parse(JSON.stringify(def.defaults)) };
    setBlocks((bs) => {
      const next = [...bs];
      if (atIdx == null) next.push(block);
      else next.splice(atIdx + 1, 0, block);
      return next;
    });
    setShowInsert(false);
  };

  if (loading || !page) {
    return <div className="p-12 text-center text-muted-foreground">Loading…</div>;
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="sm" onClick={() => navigate('/admin/site')} data-testid="back-to-pages">
            <ArrowLeft size={14} className="mr-1" /> Pages
          </Button>
          <div>
            <Input
              value={page.title}
              onChange={(e) => setPage((p) => ({ ...p, title: e.target.value }))}
              className="text-2xl font-semibold border-none p-0 h-auto focus-visible:ring-0 bg-transparent"
              data-testid="page-title-input"
            />
            <div className="text-xs text-muted-foreground flex items-center gap-2">
              <span>URL: /{page.slug}</span>
              {page.published && <Badge className="bg-green-100 text-green-800 border-green-300 border">Live</Badge>}
            </div>
          </div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => window.open(`/${page.slug === 'home' ? '' : page.slug}`, '_blank')} data-testid="preview-page">
            <Eye size={14} className="mr-1" /> Preview live
          </Button>
          <Button variant="outline" size="sm" onClick={remove} className="text-red-600 hover:text-red-700" data-testid="delete-page">
            <Trash2 size={14} className="mr-1" /> Delete
          </Button>
          <Button variant="outline" size="sm" onClick={save} disabled={saving} data-testid="save-draft">
            <Save size={14} className="mr-1" /> {saving ? 'Saving…' : 'Save draft'}
          </Button>
          <Button size="sm" onClick={publish} disabled={publishing} data-testid="publish-btn">
            <Globe size={14} className="mr-1" /> {publishing ? 'Publishing…' : 'Publish'}
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-[1fr,1fr] gap-4 h-[calc(100vh-220px)]">
        {/* Left: block list + form */}
        <div className="flex flex-col gap-3 overflow-auto pr-2">
          <Card className="p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">Blocks on this page</div>
              <Button size="sm" variant="outline" onClick={() => setShowInsert(true)} data-testid="add-block-btn">
                <Plus size={13} className="mr-1" /> Add block
              </Button>
            </div>
            {blocks.length === 0 && (
              <p className="text-xs text-muted-foreground text-center py-6">No blocks yet — click "Add block".</p>
            )}
            {blocks.map((b, idx) => {
              const def = BLOCK_DEFS.find((d) => d.type === b.type);
              const Icon = def?.icon || FileText;
              return (
                <div
                  key={b.id}
                  className={`flex items-center gap-2 px-2 py-2 rounded-md text-sm cursor-pointer border ${selectedIdx === idx ? 'border-primary bg-primary/5' : 'border-transparent hover:bg-muted'}`}
                  onClick={() => setSelectedIdx(idx)}
                  data-testid={`block-row-${idx}`}
                >
                  <Icon size={14} className="text-muted-foreground shrink-0" />
                  <span className="flex-1 truncate">
                    {def?.label || b.type}
                    <span className="text-muted-foreground text-xs ml-2">
                      {b.data?.heading || b.data?.quote?.slice(0, 30) || b.data?.eyebrow || ''}
                    </span>
                  </span>
                  <button onClick={(e) => { e.stopPropagation(); moveBlock(idx, -1); }} disabled={idx === 0} className="p-0.5 disabled:opacity-30" data-testid={`block-up-${idx}`}><ChevronUp size={14} /></button>
                  <button onClick={(e) => { e.stopPropagation(); moveBlock(idx, 1); }} disabled={idx === blocks.length - 1} className="p-0.5 disabled:opacity-30" data-testid={`block-down-${idx}`}><ChevronDown size={14} /></button>
                  <button onClick={(e) => { e.stopPropagation(); deleteBlock(idx); }} className="p-0.5 text-red-500" data-testid={`block-delete-${idx}`}><Trash2 size={13} /></button>
                </div>
              );
            })}
          </Card>

          {selectedIdx != null && blocks[selectedIdx] && (
            <Card className="p-4">
              <BlockForm
                block={blocks[selectedIdx]}
                onChange={(patch) => updateBlock(selectedIdx, patch)}
              />
            </Card>
          )}

          <Card className="p-3">
            <details>
              <summary className="cursor-pointer text-sm font-medium">Page settings (SEO, URL)</summary>
              <div className="mt-3 space-y-2">
                <div>
                  <Label className="text-xs">URL slug</Label>
                  <Input value={page.slug} onChange={(e) => setPage((p) => ({ ...p, slug: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">Section</Label>
                  <Input value={page.section || ''} onChange={(e) => setPage((p) => ({ ...p, section: e.target.value }))} />
                </div>
                <div>
                  <Label className="text-xs">SEO description</Label>
                  <Textarea rows={2} value={page.meta_description || ''} onChange={(e) => setPage((p) => ({ ...p, meta_description: e.target.value }))} />
                </div>
              </div>
            </details>
          </Card>
        </div>

        {/* Right: live preview */}
        <Card className="overflow-auto bg-[#fafaf7]" style={{ '--site-primary': '#1a3a5c' }}>
          <div className="bg-white border-b px-4 py-2 text-xs text-muted-foreground sticky top-0 z-10 flex items-center gap-2">
            <Eye size={13} /> Live preview (changes here are draft until you click Publish)
          </div>
          <div className="bg-[#fafaf7]">
            {blocks.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">No blocks yet</div>
            ) : (
              blocks.map((b) => <BlockRenderer key={b.id} block={b} />)
            )}
          </div>
        </Card>
      </div>

      {showInsert && <InsertBlockDialog onClose={() => setShowInsert(false)} onPick={(t) => insertBlock(t, selectedIdx)} />}
    </div>
  );
}

function InsertBlockDialog({ onClose, onPick }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 grid place-items-center p-4">
      <Card className="w-full max-w-2xl p-6">
        <h2 className="font-heading text-xl font-semibold mb-4">Add a block</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {BLOCK_DEFS.map((d) => {
            const Icon = d.icon;
            return (
              <button
                key={d.type}
                onClick={() => onPick(d.type)}
                className="border rounded-lg p-3 text-left hover:border-primary hover:bg-primary/5 transition"
                data-testid={`insert-block-${d.type}`}
              >
                <Icon size={20} className="mb-2 text-primary" />
                <div className="font-medium text-sm">{d.label}</div>
              </button>
            );
          })}
        </div>
        <div className="flex justify-end mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </Card>
    </div>
  );
}

// =============================================================
// BLOCK-TYPE-SPECIFIC FORMS
// =============================================================
function Field({ label, hint, children }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
      {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  );
}

function BlockForm({ block, onChange }) {
  const d = block.data || {};
  const set = (k) => (e) => onChange({ [k]: e?.target ? e.target.value : e });

  switch (block.type) {
    case 'hero':
      return (
        <div className="space-y-3">
          <h3 className="font-semibold">Hero</h3>
          <Field label="Eyebrow (small text above heading)"><Input value={d.eyebrow || ''} onChange={set('eyebrow')} /></Field>
          <Field label="Heading *"><Input value={d.heading || ''} onChange={set('heading')} /></Field>
          <Field label="Subheading"><Textarea rows={2} value={d.subheading || ''} onChange={set('subheading')} /></Field>
          <Field label="Background image (optional)">
            <ImagePicker value={d.image_url} onChange={(v) => onChange({ image_url: v })} testId="hero-image" />
          </Field>
          <div className="grid grid-cols-2 gap-2">
            <Field label="Primary button label"><Input value={d.primary_cta_label || ''} onChange={set('primary_cta_label')} /></Field>
            <Field label="Primary button link"><Input value={d.primary_cta_url || ''} onChange={set('primary_cta_url')} placeholder="/careers" /></Field>
            <Field label="Secondary button label"><Input value={d.secondary_cta_label || ''} onChange={set('secondary_cta_label')} /></Field>
            <Field label="Secondary button link"><Input value={d.secondary_cta_url || ''} onChange={set('secondary_cta_url')} placeholder="/login" /></Field>
          </div>
        </div>
      );

    case 'rich_text':
      return (
        <div className="space-y-3">
          <h3 className="font-semibold">Rich text</h3>
          <Field label="Heading (optional)"><Input value={d.heading || ''} onChange={set('heading')} /></Field>
          <Field label="Body" hint="Plain text — line breaks are preserved.">
            <Textarea rows={10} value={d.body_markdown || ''} onChange={set('body_markdown')} />
          </Field>
        </div>
      );

    case 'service_grid':
      return <ServiceGridForm d={d} onChange={onChange} />;

    case 'quote':
      return (
        <div className="space-y-3">
          <h3 className="font-semibold">Quote</h3>
          <Field label="Quote text"><Textarea rows={4} value={d.quote || ''} onChange={set('quote')} /></Field>
          <Field label="Attribution"><Input value={d.attribution || ''} onChange={set('attribution')} placeholder="e.g. American Physical Therapy Association" /></Field>
        </div>
      );

    case 'video':
      return <VideoForm d={d} onChange={onChange} />;

    case 'two_column':
      return (
        <div className="space-y-3">
          <h3 className="font-semibold">Two-column</h3>
          <Field label="Heading"><Input value={d.heading || ''} onChange={set('heading')} /></Field>
          <Field label="Body"><Textarea rows={6} value={d.body_markdown || ''} onChange={set('body_markdown')} /></Field>
          <Field label="Image">
            <ImagePicker value={d.image_url} onChange={(v) => onChange({ image_url: v })} testId="twocol-image" />
          </Field>
          <Field label="Image side">
            <select value={d.image_side || 'right'} onChange={set('image_side')} className="w-full px-3 py-2 border rounded-md text-sm">
              <option value="right">Right</option>
              <option value="left">Left</option>
            </select>
          </Field>
        </div>
      );

    case 'cta_banner':
      return (
        <div className="space-y-3">
          <h3 className="font-semibold">CTA banner</h3>
          <Field label="Heading"><Input value={d.heading || ''} onChange={set('heading')} /></Field>
          <Field label="Subheading"><Input value={d.subheading || ''} onChange={set('subheading')} /></Field>
          <Field label="Button label"><Input value={d.cta_label || ''} onChange={set('cta_label')} /></Field>
          <Field label="Button link"><Input value={d.cta_url || ''} onChange={set('cta_url')} /></Field>
          <Field label="Background color (optional)" hint="Hex color e.g. #1a3a5c. Leave blank to use brand color."><Input value={d.background_color || ''} onChange={set('background_color')} placeholder="#1a3a5c" /></Field>
        </div>
      );

    case 'contact_form':
      return (
        <div className="space-y-3">
          <h3 className="font-semibold">Contact form</h3>
          <Field label="Heading"><Input value={d.heading || ''} onChange={set('heading')} /></Field>
          <Field label="Subheading"><Input value={d.subheading || ''} onChange={set('subheading')} /></Field>
          <Field label="Submit button label"><Input value={d.submit_label || ''} onChange={set('submit_label')} /></Field>
        </div>
      );

    case 'spacer':
      return (
        <div className="space-y-3">
          <h3 className="font-semibold">Spacer</h3>
          <Field label="Size">
            <select value={d.size || 'md'} onChange={set('size')} className="w-full px-3 py-2 border rounded-md text-sm">
              <option value="sm">Small</option><option value="md">Medium</option><option value="lg">Large</option>
            </select>
          </Field>
        </div>
      );

    default:
      return <div>Editor for type "{block.type}" not implemented yet.</div>;
  }
}

function ServiceGridForm({ d, onChange }) {
  const items = d.items || [];
  const setItems = (next) => onChange({ items: next });
  return (
    <div className="space-y-3">
      <h3 className="font-semibold">Service grid (3-col)</h3>
      <Field label="Heading"><Input value={d.heading || ''} onChange={(e) => onChange({ heading: e.target.value })} /></Field>
      <Field label="Subheading"><Input value={d.subheading || ''} onChange={(e) => onChange({ subheading: e.target.value })} /></Field>
      <div className="space-y-2 pt-2 border-t">
        {items.map((it, idx) => (
          <Card key={idx} className="p-3 space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-xs text-muted-foreground">Item {idx + 1}</span>
              <button onClick={() => setItems(items.filter((_, i) => i !== idx))} className="text-red-500"><Trash2 size={13} /></button>
            </div>
            <Input value={it.title || ''} placeholder="Title" onChange={(e) => { const n = [...items]; n[idx] = { ...n[idx], title: e.target.value }; setItems(n); }} />
            <Textarea rows={2} value={it.description || ''} placeholder="Description" onChange={(e) => { const n = [...items]; n[idx] = { ...n[idx], description: e.target.value }; setItems(n); }} />
            <div>
              <span className="text-xs text-muted-foreground">Image (optional — leave blank for a soft gradient)</span>
              <ImagePicker
                value={it.image_url}
                onChange={(v) => { const n = [...items]; n[idx] = { ...n[idx], image_url: v }; setItems(n); }}
                testId={`service-item-${idx}-image`}
              />
            </div>
            <Input value={it.link_url || ''} placeholder="Link URL (e.g. /services/physical)" onChange={(e) => { const n = [...items]; n[idx] = { ...n[idx], link_url: e.target.value }; setItems(n); }} />
          </Card>
        ))}
        <Button variant="outline" size="sm" onClick={() => setItems([...items, { title: 'New item', description: '', image_url: '', link_url: '' }])}>
          <Plus size={12} className="mr-1" /> Add item
        </Button>
      </div>
    </div>
  );
}

function VideoForm({ d, onChange }) {
  const embed = useMemo(() => resolveVideoEmbed(d.url || ''), [d.url]);
  return (
    <div className="space-y-3">
      <h3 className="font-semibold">Video embed</h3>
      <Field label="Heading (optional)"><Input value={d.heading || ''} onChange={(e) => onChange({ heading: e.target.value })} /></Field>
      <Field
        label="Video URL"
        hint="Paste a Google Drive, YouTube, or Vimeo URL. For Google Drive, the file must be set to 'Anyone with the link can view'."
      >
        <Input value={d.url || ''} onChange={(e) => onChange({ url: e.target.value })} placeholder="https://drive.google.com/file/d/…/view" />
      </Field>
      {d.url && (
        <div className="text-xs text-muted-foreground">
          {embed
            ? <span className="text-green-700">✓ Embed URL detected — preview will appear on the right.</span>
            : <span className="text-amber-700">Unable to detect a video provider. Use a Google Drive, YouTube, or Vimeo share URL.</span>}
        </div>
      )}
      <Field label="Caption (optional)"><Input value={d.caption || ''} onChange={(e) => onChange({ caption: e.target.value })} /></Field>
    </div>
  );
}

// =============================================================
// NAVIGATION EDITOR
// =============================================================
function NavigationEditor({ nav, onSaved, pages }) {
  const [items, setItems] = useState(nav);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setItems(nav); setDirty(false); }, [nav]);

  const update = (next) => { setItems(next); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/cms/navigation`, { items });
      toast.success('Navigation saved');
      setDirty(false);
      onSaved?.();
    } catch (e) {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const addItem = () => {
    update([...items, { id: crypto.randomUUID(), label: 'New link', slug: null, external_url: null, children: [] }]);
  };
  const removeItem = (idx) => update(items.filter((_, i) => i !== idx));
  const move = (idx, dir) => {
    const next = [...items];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    update(next);
  };

  return (
    <Card className="p-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-heading text-lg font-semibold">Top navigation</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={addItem}><Plus size={13} className="mr-1" /> Add link</Button>
          {dirty && <Button size="sm" onClick={save} disabled={saving} data-testid="save-nav"><Save size={13} className="mr-1" /> {saving ? 'Saving…' : 'Save nav'}</Button>}
        </div>
      </div>
      <div className="space-y-2">
        {items.map((it, idx) => (
          <NavItemEditor
            key={it.id}
            item={it}
            pages={pages}
            onChange={(next) => { const arr = [...items]; arr[idx] = next; update(arr); }}
            onDelete={() => removeItem(idx)}
            onMoveUp={() => move(idx, -1)}
            onMoveDown={() => move(idx, 1)}
            canMoveUp={idx > 0}
            canMoveDown={idx < items.length - 1}
          />
        ))}
        {items.length === 0 && <p className="text-sm text-muted-foreground py-4 text-center">No nav items yet.</p>}
      </div>
    </Card>
  );
}

function NavItemEditor({ item, pages, onChange, onDelete, onMoveUp, onMoveDown, canMoveUp, canMoveDown }) {
  const addChild = () => onChange({ ...item, children: [...(item.children || []), { id: crypto.randomUUID(), label: 'New', slug: null, external_url: null, children: [] }] });
  const updateChild = (idx, next) => {
    const ch = [...(item.children || [])];
    ch[idx] = next;
    onChange({ ...item, children: ch });
  };
  const removeChild = (idx) => onChange({ ...item, children: (item.children || []).filter((_, i) => i !== idx) });

  return (
    <div className="border rounded-md p-2 space-y-2 bg-muted/20">
      <div className="flex items-center gap-2">
        <Input value={item.label || ''} onChange={(e) => onChange({ ...item, label: e.target.value })} placeholder="Label" className="flex-1" />
        <PageOrUrlPicker value={item} pages={pages} onChange={(patch) => onChange({ ...item, ...patch })} />
        <button onClick={onMoveUp} disabled={!canMoveUp} className="p-1 disabled:opacity-30"><ChevronUp size={14} /></button>
        <button onClick={onMoveDown} disabled={!canMoveDown} className="p-1 disabled:opacity-30"><ChevronDown size={14} /></button>
        <button onClick={onDelete} className="p-1 text-red-500"><Trash2 size={13} /></button>
      </div>
      <div className="ml-4 space-y-1">
        {(item.children || []).map((c, idx) => (
          <div key={c.id} className="flex items-center gap-2">
            <span className="text-muted-foreground text-xs">↳</span>
            <Input value={c.label || ''} onChange={(e) => updateChild(idx, { ...c, label: e.target.value })} className="flex-1 h-8" placeholder="Child label" />
            <PageOrUrlPicker value={c} pages={pages} onChange={(patch) => updateChild(idx, { ...c, ...patch })} />
            <button onClick={() => removeChild(idx)} className="p-1 text-red-500"><Trash2 size={12} /></button>
          </div>
        ))}
        <button onClick={addChild} className="text-xs text-primary hover:underline ml-4">+ Add sub-link</button>
      </div>
    </div>
  );
}

function PageOrUrlPicker({ value, pages, onChange }) {
  const [mode, setMode] = useState(value.external_url ? 'url' : 'page');
  return (
    <div className="flex items-center gap-1">
      <select value={mode} onChange={(e) => setMode(e.target.value)} className="text-xs px-1 py-1 border rounded">
        <option value="page">Page</option>
        <option value="url">URL</option>
      </select>
      {mode === 'page' ? (
        <select
          value={value.slug || ''}
          onChange={(e) => onChange({ slug: e.target.value || null, external_url: null })}
          className="text-xs px-2 py-1 border rounded min-w-[140px]"
        >
          <option value="">— pick a page —</option>
          {pages.map((p) => <option key={p.id} value={p.slug}>{p.title} (/{p.slug})</option>)}
        </select>
      ) : (
        <Input
          value={value.external_url || ''}
          onChange={(e) => onChange({ external_url: e.target.value, slug: null })}
          placeholder="/careers or https://…"
          className="h-7 text-xs w-44"
        />
      )}
    </div>
  );
}
